/**
 * The target rows (Option C, C2): the field that says where the connection goes — Server, Host, Database file, Cluster
 * URL, Storage account / Resource URL (`targetLabel`) — with its Browse… / Browse Azure button, and Port on its own
 * row. Both render only while their rule applies (Oracle's TNS modes have neither, C0). Blur keeps today's behaviour:
 * Kusto normalizes the cluster URL, Azure Storage reads a pasted resource URL into scope / endpoint / SAS, the network
 * engines fill their default host.
 */
import type { ReactNode } from 'react';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { isKustoProxyUrl, normalizeKustoClusterUrl } from '@/core/engines/kustoUrl';
import type { AzureDiscoveredResource } from '@/types/azureDiscovery';
import { DEFAULT_PORTS, type ConnectionFormValues, type DatabaseEngine } from '../../components/connectionFormSchema';
import { DEFAULT_SERVERS } from '../../components/connectionFieldDefaults';
import { storageServerBlurValues } from '../../components/azureStoragePreset';
import { AzureStorageEndpointHint } from '../../components/AzureStorageEndpointHint';
import { AzureBrowseButton } from '../../azure-picker';
import { fieldApplies } from '../state/connectionFieldRules';
import { revokeTnsServerProvenance } from '../state/engineDrafts';
import { ruleValidators } from '../state/fieldRuleValidators';
import { FormRow, rowLabelId } from '../pane/FormRow';
import { FieldError, INVALID_CLASS, type ConnectionFormApi } from './fieldControls';
import { targetLabel } from './fieldLabels';

type Values = Partial<ConnectionFormValues>;

export type ServerRowProps = {
  form: ConnectionFormApi;
  engine: DatabaseEngine;
  /** The label on screen (Azure Storage: for the mode the user picked, which may not be in the values yet). */
  label: string;
  onAzurePick: (resource: AzureDiscoveredResource) => void;
  entryDisabledReason?: string;
  /** "Press Tab to fill …" for this field (the validation gate's hint). */
  defaultHint?: { field: string; value: string } | null;
  /** The engine whose draft the host was copied from on this engine's first visit, while untouched (UX-20). */
  serverCarriedFrom?: string | null;
  onDismissDefaultHint?: () => void;
  /** Extra lines under the field (Azure Storage: the mode's line). */
  extra?: ReactNode;
};

const PLACEHOLDERS: Partial<Record<DatabaseEngine, string>> = {
  sqlite: 'Select a .sqlite or .db file…',
  // Short enough to show in full (final UX review UX-12).
  azurestorage: 'contoso, or a container / blob URL',
};

const HINTS: Partial<Record<DatabaseEngine, string>> = {
  mssql: 'Host name or IP. Use host\\instance for a named instance.',
  sqlite: 'An existing .db or .sqlite file on this computer.',
};

async function pickSqliteFile(form: ConnectionFormApi): Promise<void> {
  try {
    if (!window.electronAPI?.sqlite?.openDatabaseFile) throw new Error('SQLite file dialog is not available');
    const result = await window.electronAPI.sqlite.openDatabaseFile({});
    if (!result.success) return;
    const filePath = result.filePath;
    form.setFieldValue('server', filePath);
    if (!String(form.state?.values?.alias ?? '').trim()) {
      form.setFieldValue('alias', filePath.split(/[/\\]/).pop() || filePath);
    }
  } catch (error) {
    console.error('Failed to select SQLite file:', error);
  }
}

export function ServerRow({ form, engine, label, onAzurePick, entryDisabledReason, defaultHint, onDismissDefaultHint, serverCarriedFrom, extra }: ServerRowProps) {
  return (
    <form.Field name="server" validators={ruleValidators('server', ['onChange', 'onSubmit'])}>
      {(field: any) => (
        <FormRow row="server" label={label} htmlFor="server" hint={HINTS[engine]}>
          <div className="flex items-center gap-2">
            <Input
              id="server"
              name="server"
              placeholder={PLACEHOLDERS[engine] ?? DEFAULT_SERVERS[engine] ?? ''}
              className={field.state.meta.errors.length ? INVALID_CLASS : undefined}
              value={field.state.value ?? ''}
              onChange={(event) => {
                onDismissDefaultHint?.();
                field.handleChange(event.target.value);
                revokeTnsServerProvenance(form);
              }}
              onBlur={(event) => {
                if (engine === 'kusto') {
                  field.handleChange(normalizeKustoClusterUrl(event.target.value));
                } else if (engine === 'azurestorage') {
                  // A bare account name stays as typed; a container / blob / table / queue / share URL becomes a
                  // single-resource attachment (endpoint + service + SAS secret) — spec §4.1.
                  const derived = storageServerBlurValues(event.target.value, form.state.values);
                  for (const [name, value] of Object.entries(derived)) {
                    if (name === 'server') field.handleChange(value);
                    else form.setFieldValue(name, value);
                  }
                } else if (engine !== 'sqlite' && !field.state.value?.trim() && DEFAULT_SERVERS[engine]) {
                  field.handleChange(DEFAULT_SERVERS[engine]);
                  revokeTnsServerProvenance(form);
                }
                onDismissDefaultHint?.();
                field.handleBlur();
              }}
              data-testid="server-input"
            />
            {engine === 'sqlite' && (
              <Button type="button" variant="outline" aria-describedby={rowLabelId('server')} onClick={() => void pickSqliteFile(form)} data-testid="sqlite-browse-button">
                Browse…
              </Button>
            )}
            <AzureBrowseButton engine={engine} onPick={onAzurePick} disabledReason={entryDisabledReason} describedBy={rowLabelId('server')} />
          </div>
          <FieldError meta={field.state.meta} />
          {serverCarriedFrom && (
            // The plan's carry-over (server, login and SSH between network engines on a first visit), said once (UX-20).
            <p className="mt-1 text-xs text-muted-foreground" data-testid="server-carried-note">
              Copied from your {serverCarriedFrom} draft.
            </p>
          )}
          {defaultHint?.field === 'server' && (
            <p className="mt-1 text-sm text-primary" data-testid="server-default-hint">
              Press <kbd className="rounded border border-primary/40 bg-primary/10 px-1 font-sans text-xs">Tab</kbd> to fill{' '}
              <span className="font-mono">&lsquo;{defaultHint.value}&rsquo;</span>
            </p>
          )}
          {engine === 'azurestorage' && <AzureStorageEndpointHint form={form} />}
          {engine === 'kusto' && isKustoProxyUrl(String(field.state.value ?? '')) && (
            <p className="mt-1 text-xs text-muted-foreground" data-testid="kusto-proxy-helper">
              App Insights / Log Analytics proxy — the resource&apos;s tenant sign-in applies.
            </p>
          )}
          {extra}
        </FormRow>
      )}
    </form.Field>
  );
}

export function PortRow({ form, engine }: { form: ConnectionFormApi; engine: DatabaseEngine }) {
  // Windows + SQL Server: '<auto>' (SQL Tools Service handles local protocols); elsewhere TCP is the only option.
  const isWindows = typeof navigator !== 'undefined' && navigator.platform?.toLowerCase().includes('win');
  const placeholder = isWindows && engine === 'mssql' ? '<auto>' : `${DEFAULT_PORTS[engine]}`;
  return (
    <form.Field name="port" validators={ruleValidators('port', ['onChange', 'onSubmit'])}>
      {(field: any) => (
        <FormRow row="port" label="Port" htmlFor="port">
          <Input
            id="port"
            name="port"
            placeholder={placeholder}
            className={`w-28 ${field.state.meta.errors.length ? INVALID_CLASS : ''}`}
            value={field.state.value === '<auto>' ? '' : String(field.state.value ?? '')}
            onChange={(event) => field.handleChange(event.target.value)}
            onBlur={() => {
              if (!String(field.state.value ?? '').trim()) field.handleChange('<auto>');
              field.handleBlur();
            }}
            data-testid="port-input"
          />
          <FieldError meta={field.state.meta} />
        </FormRow>
      )}
    </form.Field>
  );
}

/** Server and Port, each while its rule applies (Oracle's TNS modes: neither). */
export function ServerAndPortRows(props: Omit<ServerRowProps, 'label'>) {
  const { form, engine } = props;
  return (
    <form.Subscribe selector={(state: { values: Values }) => ({ server: fieldApplies('server', state.values), port: fieldApplies('port', state.values), label: targetLabel(state.values) })}>
      {(applies: { server: boolean; port: boolean; label: string }) => (
        <>
          {applies.server && <ServerRow {...props} label={applies.label} />}
          {applies.port && <PortRow form={form} engine={engine} />}
        </>
      )}
    </form.Subscribe>
  );
}
